import type { WireColor } from '@/api/types/color'
import { resolvePaletteFill, tintedPaletteHex } from '@/lib/colors/resolve-palette-color'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

export type FillPaint = Pick<ShapeData, 'fill' | 'fillColorId' | 'fillTint'>

/** The None swatch shared by every color popover (`colorId: -1`, `hexValue: 'none'`). */
export const NONE_COLOR: WireColor = {
  colorId: -1,
  colorName: 'None',
  hexValue: 'none',
  colorType: 'SYSTEM',
  pageEditorSort: 0,
}

/** Returns true for the None sentinel (`colorId: -1` or `hexValue: 'none'`). */
export function isNoneColor(color: WireColor): boolean {
  return color.colorId === -1 || color.hexValue === 'none'
}

/**
 * The fill fields a pick writes. `fill` mirrors the palette color as CSS so creation previews and
 * the color indicator match before the canvas resolves `fillColorId`; None clears the id so the
 * wire omits `fillStyle`. Keys are always present (even when undefined) so an undo patch resets them.
 */
export function fillPaintForColor(color: WireColor, tint: number): FillPaint {
  if (isNoneColor(color)) {
    return { fill: 'transparent', fillColorId: undefined, fillTint: undefined }
  }

  return {
    fill: tintedPaletteHex(color, tint) ?? color.hexValue,
    fillColorId: color.colorId,
    fillTint: tint,
  }
}

/** A shape's current fill fields, as an undo `before` patch. */
export function fillPaintOf(shape: ShapeData): FillPaint {
  return { fill: shape.fill, fillColorId: shape.fillColorId, fillTint: shape.fillTint }
}

/** Retint a palette fill, keeping its color; null when the fill has no palette color to tint. */
export function retintFillPaint(
  paint: FillPaint,
  tint: number,
  palette: ReadonlyMap<number, WireColor>
): FillPaint | null {
  if (paint.fillColorId == null) return null

  return {
    fill: resolvePaletteFill(palette, paint.fillColorId, tint) ?? paint.fill,
    fillColorId: paint.fillColorId,
    fillTint: tint,
  }
}
